import React from 'react';
import { Helmet } from 'react-helmet-async';
import { companyInfo, serviceImages } from '../mock';

const SEOHead = ({ 
  title, 
  description, 
  keywords, 
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  structuredData = null,
  noindex = false
}) => {
  const siteName = "SET-G";
  const defaultTitle = `${siteName} | Expert Télécoms & IT en Côte d'Ivoire | Audit QoS, Ingénierie Radio`;
  const defaultDescription = `SET-G (Strategies Engineerings and Technologies Global) - Leader en audit QoS, ingénierie radio et solutions IT en Côte d'Ivoire. +255 localités auditées, +14 projets réalisés pour ARTCI, ORANGE CI, SIR. Contactez-nous: +225 27 22 34 98 15`;
  const defaultKeywords = 'SET-G, audit QoS, audit qualité de service, ingénierie radio, télécommunications Côte d\'Ivoire, ARTCI, réseaux mobiles 2G 3G 4G, benchmarking QoS, infrastructure IT, formation télécoms, Orange CI, digitalisation, expert télécom Abidjan, contrôle qualité réseau mobile';
  const heroImage = "https://customer-assets.emergentagent.com/job_bbf88a63-9ea0-4f33-b8a4-13e4c3715502/artifacts/bwb2vqa2_SET-G_Slide1_Final.png";
  
  const pageTitle = title ? `${title} | ${siteName}` : defaultTitle;
  const pageDescription = description || defaultDescription;
  const pageKeywords = keywords || defaultKeywords;
  const pageOgTitle = ogTitle || pageTitle;
  const pageOgDescription = ogDescription || pageDescription;
  const pageOgImage = ogImage || heroImage;
  
  const siteUrl = "https://www.set-g.com";
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
  const currentUrl = canonicalUrl || `${siteUrl}${currentPath}`;

  // Schema.org Organization
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${siteUrl}/#organization`,
    "name": "SET-G",
    "alternateName": "Strategies Engineerings and Technologies Global",
    "legalName": "SET-G SARL",
    "url": siteUrl,
    "logo": {
      "@type": "ImageObject",
      "url": companyInfo.logo,
      "width": 200,
      "height": 60
    },
    "image": heroImage,
    "description": companyInfo.description,
    "slogan": companyInfo.slogan,
    "foundingDate": "2020",
    "founder": {
      "@type": "Person",
      "name": "Georges KASSI",
      "jobTitle": "Gérant & Fondateur",
      "description": "Plus de 25 ans d'expérience dans l'industrie des télécoms"
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Angré Face Soleil 3, immeuble restaurant Wallace",
      "addressLocality": "Abidjan Cocody",
      "postalCode": "06 BP 2179 Abidjan 06",
      "addressCountry": "CI"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "5.3908181",
      "longitude": "-3.9750726"
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "telephone": "+225-27-22-34-98-15",
        "contactType": "customer service",
        "availableLanguage": ["French"],
        "areaServed": "CI"
      },
      {
        "@type": "ContactPoint",
        "telephone": "+225-07-57-20-73-19",
        "contactType": "sales",
        "availableLanguage": ["French"],
        "areaServed": "CI"
      }
    ],
    "email": "contact@set-g.com",
    "sameAs": [],
    "numberOfEmployees": {
      "@type": "QuantitativeValue",
      "minValue": 10,
      "maxValue": 50
    },
    "areaServed": [
      {
        "@type": "Country",
        "name": "Côte d'Ivoire"
      },
      {
        "@type": "Continent", 
        "name": "Afrique"
      }
    ],
    "knowsAbout": [
      "Audit QoS",
      "Ingénierie Radio",
      "Télécommunications",
      "Réseaux mobiles 2G/3G/4G",
      "Infrastructure IT",
      "Data Center",
      "Virtualisation"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Services SET-G",
      "itemListElement": [
        {
          "@type": "OfferCatalog",
          "name": "Services Télécoms",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Audit et Benchmarking QoS/QoE",
                "description": "Audit de la qualité de service des réseaux de téléphonie mobile. Benchmarking comparatif entre opérateurs. Évaluation de la QoE.",
                "provider": {"@id": `${siteUrl}/#organization`}
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Ingénierie Radio",
                "description": "Planification, design et optimisation des réseaux mobiles 2G/3G/4G. Dimensionnement, site survey, SSV et optimisation.",
                "provider": {"@im": `${siteUrl}/#organization`}
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Rollout Télécoms",
                "description": "Installation des équipements télécoms (BTS, NodeB, eNodeB), génie civil, solutions énergie.",
                "provider": {"@id": `${siteUrl}/#organization`}
              }
            }
          ]
        },
        {
          "@type": "OfferCatalog",
          "name": "Services Informatiques",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Data Center et Virtualisation",
                "description": "Étude de faisabilité, définition d'architecture et implémentation de data centers.",
                "provider": {"@id": `${siteUrl}/#organization`}
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Infrastructure Systèmes et Réseaux",
                "description": "Conception et déploiement d'infrastructures IT haute disponibilité.",
                "provider": {"@id": `${siteUrl}/#organization`}
              }
            }
          ]
        },
        {
          "@type": "OfferCatalog",
          "name": "Services Professionnels",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Formations Techniques",
                "description": "Formations en télécommunications (2G/3G/4G) et informatique (réseaux, bases de données, sécurité).",
                "provider": {"@id": `${siteUrl}/#organization`}
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Conseil Télécom et IT",
                "description": "Conseil en stratégie digitale, optimisation des coûts, développement d'Entreprise.",
                "provider": {"@id": `${siteUrl}/#organization`}
              }
            }
          ]
        }
      ]
    }
  };

  // Schema.org WebSite
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${siteUrl}/#website`,
    "url": siteUrl,
    "name": "SET-G",
    "description": defaultDescription,
    "publisher": {"@id": `${siteUrl}/#organization`},
    "inLanguage": "fr-CI"
  };

  // Schema.org LocalBusiness
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${siteUrl}/#localbusiness`,
    "name": "SET-G",
    "image": heroImage,
    "url": siteUrl,
    "telephone": "+225-27-22-34-98-15",
    "email": "contact@set-g.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Angré Face Soleil 3, immeuble restaurant Wallace",
      "addressLocality": "Abidjan Cocody",
      "addressCountry": "CI"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "5.3908181",
      "longitude": "-3.9750726"
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "08:00",
      "closes": "17:00"
    },
    "priceRange": "$$",
    "currenciesAccepted": "XOF",
    "paymentAccepted": "Cash, Bank Transfer"
  };

  // Combine all schemas
  const allSchemas = structuredData ? [structuredData] : [organizationSchema, websiteSchema, localBusinessSchema];

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="title" content={pageTitle} />
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <meta name="author" content="SET-G - Strategies Engineerings and Technologies Global" />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"} />
      <meta name="googlebot" content={noindex ? "noindex, nofollow" : "index, follow"} />
      <meta name="bingbot" content={noindex ? "noindex, nofollow" : "index, follow"} />
      
      {/* Language and Region */}
      <html lang="fr" />
      <meta http-equiv="content-language" content="fr-CI" />
      <meta name="language" content="French" />
      <meta name="geo.region" content="CI-AB" />
      <meta name="geo.placename" content="Abidjan, Côte d'Ivoire" />
      <meta name="geo.position" content="5.3908181;-3.9750726" />
      <meta name="ICBM" content="5.3908181, -3.9750726" />
      
      {/* Target Audience */}
      <meta name="audience" content="Entreprises, Opérateurs télécoms, Organismes publics, Régulateurs" />
      <meta name="coverage" content="Côte d'Ivoire, Afrique de l'Ouest, Afrique" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />
      
      {/* Alternate URLs */}
      <link rel="alternate" hrefLang="fr" href={currentUrl} />
      <link rel="alternate" hrefLang="fr-CI" href={currentUrl} />
      <link rel="alternate" hrefLang="x-default" href={currentUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={pageOgTitle} />
      <meta property="og:description" content={pageOgDescription} />
      <meta property="og:image" content={pageOgImage} />
      <meta property="og:image:secure_url" content={pageOgImage} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`SET-G - ${pageTitle}`} />
      <meta property="og:site_name" content="SET-G" />
      <meta property="og:locale" content="fr_CI" />
      <meta property="og:locale:alternate" content="fr_FR" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={pageOgTitle} />
      <meta name="twitter:description" content={pageOgDescription} />
      <meta name="twitter:image" content={pageOgImage} />
      <meta name="twitter:image:alt" content={`SET-G - ${pageTitle}`} />
      
      {/* LinkedIn */}
      <meta property="og:linkedin:owner" content="SET-G" />
      
      {/* Mobile & App */}
      <meta name="theme-color" content="#ea580c" />
      <meta name="msapplication-TileColor" content="#ea580c" />
      <meta name="msapplication-navbutton-color" content="#ea580c" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="SET-G" />
      <meta name="application-name" content="SET-G" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="format-detection" content="telephone=yes" />
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
      
      {/* Favicons */}
      <link rel="icon" href="/favicon.ico" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <link rel="manifest" href="/manifest.json" />
      
      {/* Business Contact Info */}
      <meta name="contact" content="contact@set-g.com" />
      <meta name="reply-to" content="contact@set-g.com" />
      <meta name="owner" content="SET-G SARL" />
      <meta name="copyright" content="SET-G 2020-2026" />
      
      {/* Performance Hints */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://customer-assets.emergentagent.com" />
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="dns-prefetch" href="//fonts.gstatic.com" />
      <link rel="dns-prefetch" href="//customer-assets.emergentagent.com" />
      
      {/* Preload critical assets */}
      <link rel="preload" as="image" href={heroImage} />
      
      {/* Structured Data - JSON-LD */}
      {allSchemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEOHead;